import { useDebouncedState } from '@mantine/hooks';
import { createColumnHelper } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { LuHourglass, LuPlus } from 'react-icons/lu';

import { TaskStatus, TaskView } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import { useDockState } from '@/views/dock/dock-state';
import { formatDate, titleCase, useRequiredParams } from 'utils';
import { HouseholdHeaderActionsPortal } from 'views/household/HouseholdIndividualPage';
import { useListTasksByHouseholdQuery } from 'views/tasks/task-queries';
import { useTaskOverlayState } from 'views/tasks/task-state';
import { getTaskAssigneeLabel } from 'views/tasks/task-utils';
import TaskOverlays from 'views/tasks/TaskOverlays';
import { TaskStatusBadge } from 'views/tasks/widgets/TaskBadge';
import { TaskRecurrenceChip } from 'views/tasks/widgets/TaskRecurrenceChip';
import { TaskTypeIcon } from 'views/tasks/widgets/TaskTypeIcon';
import { CustomTablePage, SearchInput } from 'widgets';

import { filterTasks } from './task-utils';

const columnHelper = createColumnHelper<TaskView>();

export const HouseholdTasksTab = () => {
    const { globalAccountGUID: householdGUID } = useRequiredParams(['globalAccountGUID']);
    const tasksViewQuery = useListTasksByHouseholdQuery(householdGUID);
    const { openViewTaskSheet } = useTaskOverlayState();
    const { addCreateTaskDockItem } = useDockState();

    const [searchTerm, setSearchTerm] = useDebouncedState('', 500);

    const tasks = useMemo(() => {
        if (!tasksViewQuery.data) {
            return [];
        }
        return filterTasks(tasksViewQuery.data, { searchTerm });
    }, [tasksViewQuery.data, searchTerm]);

    const columns = useMemo(
        () => [
            columnHelper.accessor((row) => row.title || 'Untitled Task', {
                id: 'title',
                header: 'Task',
                cell: ({ row }) => (
                    <div className="flex max-w-[400px] items-center gap-2">
                        <span className="truncate">{row.original.title || 'Untitled Task'}</span>
                        <TaskRecurrenceChip frequency={row.original.frequency} />
                    </div>
                ),
            }),
            columnHelper.accessor('dueDate', {
                header: 'Due Date',
                cell: ({ row }) => {
                    const task = row.original;
                    return task.overdue && task.status !== TaskStatus.COMPLETED ? (
                        <div className="flex items-center gap-1.5" title="Overdue">
                            <LuHourglass className="h-3.5 w-3.5 shrink-0 text-red-500" />
                            <span>{formatDate(task.dueDate)}</span>
                        </div>
                    ) : (
                        <span>{formatDate(task.dueDate)}</span>
                    );
                },
            }),
            columnHelper.accessor('status', {
                header: 'Status',
                cell: ({ row }) => (
                    <div className="max-w-[160px]">
                        <TaskStatusBadge status={row.original.status} />
                    </div>
                ),
            }),
            columnHelper.accessor('type', {
                header: 'Type',
                cell: ({ row }) => (
                    <div className="flex items-center space-x-1">
                        <TaskTypeIcon type={row.original.type} />
                        <span>{titleCase(row.original.type)}</span>
                    </div>
                ),
            }),
            columnHelper.accessor((row) => getTaskAssigneeLabel(row), {
                id: 'assignee',
                header: 'Assigned To',
            }),
        ],
        [],
    );

    return (
        <div>
            <HouseholdHeaderActionsPortal>
                <div className="flex items-center gap-4">
                    <SearchInput round placeholder="Search tasks..." onChange={(e) => setSearchTerm(e.target.value)} />
                    <Button variant="outline2" size="sm" onClick={() => addCreateTaskDockItem(householdGUID)}>
                        <LuPlus className="h-4 w-4" />
                        <span>Task</span>
                    </Button>
                </div>
            </HouseholdHeaderActionsPortal>

            <CustomTablePage
                data={tasks}
                columns={columns}
                isLoading={tasksViewQuery.isLoading}
                rowOnClick={(task) => openViewTaskSheet(task.taskGUID)}
                exportName="tasks"
                cardClassName="m-0 border-0 shadow-none"
                defaultSortCol="dueDate"
                defaultSortDirection="desc"
            />

            <TaskOverlays />
        </div>
    );
};
